import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { useSharedKd } from '../../api/hooks';
import type { SharedKd } from '../../api/schemas';

/**
 * NasdileneKdPage — kontrolní dny, které přihlášenému uživateli někdo nasdílel
 * (read-only). Otevření vede na SdilenyKdView.
 * Route: /app/kontrolni-dny/nasdilene
 */
export function NasdileneKdPage() {
  const navigate = useNavigate();
  const { data: items = [], isLoading, error } = useSharedKd();

  const columns: ListColumn<SharedKd>[] = [
    { key: 'cislo', label: 'Č.', width: '90px', mono: true, dim: true, render: (k) => `KD-${String(k.cislo).padStart(2, '0')}` },
    { key: 'stavba_nazev', label: 'Stavba', render: (k) => k.stavba_nazev || '—' },
    { key: 'datum', label: 'Datum', width: '140px', mono: true, render: (k) => k.datum ? k.datum.slice(0, 10) : '—' },
    { key: 'owner', label: 'Sdílel', width: '220px', render: (k) => k.owner_name || k.owner_email || '—' },
    {
      key: 'nesplneno',
      label: 'Nesplněno',
      width: '120px',
      render: (k) =>
        k.nesplneno_count > 0 ? (
          <Badge tone="yellow" pill>{k.nesplneno_count}</Badge>
        ) : (
          <span style={{ color: 'var(--sd-text-dimmer)', fontFamily: 'var(--sd-font-mono)' }}>0</span>
        ),
    },
    {
      key: 'stav',
      label: 'Stav',
      width: '140px',
      render: (k) =>
        k.stav === 'uzavreno' ? (
          <Badge tone="success" pill>✓ Uzavřen</Badge>
        ) : (
          <Badge tone="yellow" pill dot>{k.stav || 'koncept'}</Badge>
        ),
    },
  ];

  return (
    <ListPage
      breadcrumb={['Realizace', 'Nasdílené KD']}
      title="Nasdílené kontrolní dny"
      subtitle={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} záznamů`}
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} záznamů`}
      searchPlaceholder="Hledat ve stavbě nebo datu…"
    >
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={items}
          rowKey={(k) => `${k.stavba_id}-${k.id}`}
          onRowClick={(k) => navigate(`/app/kontrolni-dny/nasdilene/${k.stavba_id}/${k.id}`)}
          emptyTitle="Žádné nasdílené KD"
          emptyText="Když vám někdo nasdílí kontrolní den, objeví se zde."
        />
      )}
    </ListPage>
  );
}
